import React, { useCallback, useEffect, useState, useContext } from 'react';
import {
  Alert, Button, DatePicker, Descriptions, Input, Popconfirm, Segmented, Spin, Table, Tag, Typography, Upload, message,
} from 'antd';
import { InboxOutlined, CloudUploadOutlined, IdcardOutlined, DatabaseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext, hasGrant } from '../App';

const { Dragger } = Upload;
const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

// The survey scores 0-100; anything under 80 is the range people chase.
const csatColor = (v) => v == null ? undefined : v >= 90 ? '#52c41a' : v >= 80 ? '#faad14' : '#ff4d4f';

function Score({ value }) {
  if (value == null) return <Text type="secondary">—</Text>;
  return <span style={{ color: csatColor(value), fontWeight: 600 }}>{value}</span>;
}

export default function CNECSATUploadPage() {
  const currentUser = useContext(UserContext);
  // Role, or an explicit grant for this page. The grant is additive, so it
  // opens the page for someone outside the CNE team and never closes it.
  const isAdmin     = currentUser?.Role === 'admin' || currentUser?.Role === 'qa_admin'
                   || (currentUser?.JobID === 6 && currentUser?.TeamID === 3)
                   || hasGrant(currentUser, 'cne-csat-upload');

  const [view, setView] = useState('upload');

  const [file,       setFile]       = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [result,     setResult]     = useState(null);

  const [range,           setRange]           = useState([dayjs().subtract(30, 'day'), dayjs()]);
  const [uploaded,        setUploaded]        = useState([]);
  const [uploadedLoading, setUploadedLoading] = useState(false);

  const [mapping,        setMapping]        = useState({ users: [], unmapped: [] });
  const [mappingLoading, setMappingLoading] = useState(false);
  const [idDraft,        setIdDraft]        = useState({});
  const [savingIds,      setSavingIds]      = useState(new Set());

  const loadUploaded = useCallback(() => {
    if (view !== 'uploaded' || !range?.[0] || !range?.[1]) return;
    setUploadedLoading(true);
    api.get('/api/admin/cne-csat/uploaded', {
      params: { date_from: range[0].format('YYYY-MM-DD'), date_to: range[1].format('YYYY-MM-DD') },
    })
      .then((r) => setUploaded(r.data))
      .catch(() => setUploaded([]))
      .finally(() => setUploadedLoading(false));
  }, [view, range]);

  useEffect(() => { loadUploaded(); }, [loadUploaded]);

  const loadMapping = useCallback(() => {
    if (view !== 'ids') return;
    setMappingLoading(true);
    api.get('/api/admin/cne-csat/team-users')
      .then((r) => {
        setMapping(r.data);
        const draft = {};
        (r.data.users || []).forEach((u) => { draft[u.ID] = u.MedalliaID || ''; });
        setIdDraft(draft);
      })
      .catch(() => message.error('Failed to load team users.'))
      .finally(() => setMappingLoading(false));
  }, [view]);

  useEffect(() => { loadMapping(); }, [loadMapping]);

  const doPreview = async (f) => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await api.post('/api/admin/cne-csat/preview', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
    } catch (err) {
      message.error(err.response?.data?.error || 'Could not read that file.');
      setFile(null);
    } finally {
      setPreviewing(false);
    }
  };

  const doCommit = async () => {
    setSaving(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await api.post('/api/admin/cne-csat/commit', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      message.success(`Saved ${res.data.inserted} responses.`);
      setPreview(null);
      setFile(null);
    } catch (err) {
      message.error(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const saveMedalliaId = async (userId) => {
    setSavingIds((prev) => new Set([...prev, userId]));
    try {
      await api.put('/api/admin/cne-csat/medallia-id', {
        user_id: userId, medallia_id: idDraft[userId] || null,
      });
      message.success('Medallia ID updated.');
      loadMapping();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to update Medallia ID.');
    } finally {
      setSavingIds((prev) => { const s = new Set(prev); s.delete(userId); return s; });
    }
  };

  const agentCols = [
    { title: 'Agent', key: 'agent', width: 240,
      render: (_, a) => (
        <span>
          {a.DisplayName
            ? <><strong>{a.DisplayName}</strong>{a.IsActive === false && <Tag color="default" style={{ marginLeft: 6 }}>Inactive</Tag>}</>
            : <Text type="warning">{a.NameInFile || '(no name in file)'} <Tag color="orange">Not mapped</Tag></Text>}
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{a.MedalliaID || '(blank)'}</Text>
        </span>
      ),
    },
    { title: 'Responses', dataIndex: 'Responses', key: 'Responses', width: 110, align: 'right',
      sorter: (a, b) => a.Responses - b.Responses },
    { title: 'Avg CSAT', dataIndex: 'AvgCSAT', key: 'AvgCSAT', width: 110, align: 'right',
      sorter: (a, b) => (a.AvgCSAT ?? -1) - (b.AvgCSAT ?? -1),
      render: (v) => <Score value={v} /> },
  ];

  const uploadedCols = [
    { title: 'Response Date', dataIndex: 'ResponseDate', key: 'ResponseDate', width: 120,
      sorter: (a, b) => (a.ResponseDate || '').localeCompare(b.ResponseDate || '') },
    { title: 'Survey ID', dataIndex: 'SurveyID', key: 'SurveyID', width: 100 },
    { title: 'Agent', key: 'agent', width: 200,
      render: (_, r) => r.DisplayName
        ? <strong>{r.DisplayName}</strong>
        : <Text type="warning">{r.AgentScoredName || r.MedalliaID} <Tag color="orange">Not mapped</Tag></Text>,
    },
    { title: 'CSAT', dataIndex: 'CSAT', key: 'CSAT', width: 80, align: 'right',
      sorter: (a, b) => (a.CSAT ?? -1) - (b.CSAT ?? -1),
      render: (v) => <Score value={v} /> },
    { title: 'Recommend', dataIndex: 'LikelihoodToRecommend', key: 'LikelihoodToRecommend', width: 110, align: 'right',
      render: (v) => <Score value={v} /> },
    { title: 'Agent Sat', dataIndex: 'AgentSatisfaction', key: 'AgentSatisfaction', width: 100, align: 'right',
      render: (v) => <Score value={v} /> },
    { title: 'State', dataIndex: 'ServiceState', key: 'ServiceState', width: 70 },
    { title: 'Group', dataIndex: 'FunctionalGroup', key: 'FunctionalGroup', width: 160, ellipsis: true },
    { title: 'Case Type', dataIndex: 'CaseType', key: 'CaseType', width: 180, ellipsis: true },
    { title: 'Subtype', dataIndex: 'Subtype', key: 'Subtype', width: 160, ellipsis: true },
    { title: 'Origin', dataIndex: 'Origin', key: 'Origin', width: 110 },
    { title: 'Alert', key: 'alert', width: 200,
      render: (_, r) => r.SurveyHasAlert === 'Yes'
        ? <Tag color="red">{r.AlertType || 'Alert'}</Tag>
        : <Text type="secondary">—</Text>,
    },
    { title: 'Resolved', dataIndex: 'InquiryResolved', key: 'InquiryResolved', width: 240, ellipsis: true,
      render: (v) => v || <Text type="secondary">—</Text> },
  ];

  const userCols = [
    { title: 'Name', key: 'name', width: 240,
      render: (_, r) => (
        <span>
          {r.DisplayName}
          {!r.IsActive && <Tag color="default" style={{ marginLeft: 8 }}>Inactive</Tag>}
        </span>
      ),
    },
    { title: 'Medallia ID', key: 'medallia',
      render: (_, r) => (
        <Input
          size="small"
          value={idDraft[r.ID] ?? ''}
          placeholder="e.g. C156895"
          style={{ fontFamily: 'monospace', fontSize: 12, maxWidth: 260 }}
          onChange={(e) => setIdDraft((prev) => ({ ...prev, [r.ID]: e.target.value }))}
          onPressEnter={() => saveMedalliaId(r.ID)}
          allowClear
        />
      ),
    },
    { title: '', key: 'save', width: 80,
      render: (_, r) => {
        const changed = (idDraft[r.ID] ?? '') !== (r.MedalliaID || '');
        return (
          <Button
            size="small"
            type={changed ? 'primary' : 'default'}
            loading={savingIds.has(r.ID)}
            disabled={!changed}
            onClick={() => saveMedalliaId(r.ID)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0 }}>CNE CSAT Responses</Title>
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { label: 'Upload',              value: 'upload',   icon: <CloudUploadOutlined /> },
            { label: 'View Uploaded',       value: 'uploaded', icon: <DatabaseOutlined /> },
            { label: 'Update Medallia IDs', value: 'ids',      icon: <IdcardOutlined /> },
          ]}
        />
      </div>

      {/* ── UPLOAD ────────────────────────────────────────────────────────── */}
      {view === 'upload' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 20 }}>
            Upload the CSAT survey export (tab-separated). The file carries its own <strong>Response Date</strong>,
            so there is no month to pick. Only responses with a Call Center Location of <strong>Superior</strong> are
            stored — other locations in the export are skipped and counted, not rejected. Agents are matched on <strong>Agent ID</strong>, the survey tool&apos;s id, against
            the <strong>Medallia ID</strong> on each user record. Responses whose id is not mapped yet still save and link
            the moment it is set — use the <strong>Update Medallia IDs</strong> tab. Re-uploading an overlapping export
            corrects those responses rather than duplicating them.
          </Paragraph>

          {!isAdmin && (
            <Alert type="warning" message="Admin access required to upload data." style={{ marginBottom: 16 }} />
          )}

          {isAdmin && !preview && (
            <Dragger
              name="file"
              accept=".txt,.tsv,.csv"
              beforeUpload={(f) => { setFile(f); doPreview(f); return false; }}
              onRemove={() => { setFile(null); setPreview(null); }}
              fileList={file ? [file] : []}
              disabled={previewing}
              style={{ marginBottom: 16 }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">Click or drag a tab-delimited .txt file here</p>
              <p className="ant-upload-hint">Must have a &quot;Survey ID&quot; column. Nothing is saved until you confirm on the next step.</p>
            </Dragger>
          )}

          {previewing && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}

          {preview && (
            <>
              <Alert
                type={preview.unmatched?.length ? 'warning' : 'info'}
                style={{ marginBottom: 16 }}
                message={preview.unmatched?.length
                  ? `${preview.unmatched.length} Medallia ID(s) are not mapped to a user`
                  : 'Ready to save'}
                description={
                  <>
                    <Descriptions size="small" column={4} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Responses">{preview.row_count}</Descriptions.Item>
                      <Descriptions.Item label="Agents">{preview.agents.length}</Descriptions.Item>
                      <Descriptions.Item label="Dates">{preview.date_from} to {preview.date_to}</Descriptions.Item>
                      <Descriptions.Item label="Avg CSAT"><Score value={preview.avg_csat} /></Descriptions.Item>
                    </Descriptions>
                    {preview.skipped?.count > 0 && (
                      <p style={{ margin: '8px 0 0' }}>
                        <strong>{preview.skipped.count}</strong> response(s) skipped as another call
                        center&apos;s:{' '}
                        {preview.skipped.locations.map(([name, n]) => `${name} (${n})`).join(', ')}.
                        Only <strong>Superior</strong> is stored.
                      </p>
                    )}
                    {preview.unmatched?.length > 0 && (
                      <p style={{ margin: '8px 0 0' }}>
                        These save anyway and link as soon as the id is set on a user record —
                        the <strong>Update Medallia IDs</strong> tab lists them.
                      </p>
                    )}
                  </>
                }
              />

              <Title level={5} style={{ marginBottom: 8 }}>Agents in this file</Title>
              <Table
                rowKey={(a) => a.MedalliaID || a.NameInFile}
                dataSource={preview.agents}
                columns={agentCols}
                size="small"
                pagination={false}
                bordered
                style={{ marginBottom: 16 }}
              />

              <div style={{ display: 'flex', gap: 8 }}>
                <Popconfirm
                  title={`Save ${preview.row_count} responses?`}
                  description="Responses already held under these Survey IDs are replaced. Everything else is left alone."
                  okText="Save"
                  onConfirm={doCommit}
                >
                  <Button type="primary" loading={saving}>Save {preview.row_count} Responses</Button>
                </Popconfirm>
                <Button onClick={() => { setPreview(null); setFile(null); }} disabled={saving}>Cancel</Button>
              </div>
            </>
          )}

          {result && (
            <Alert
              type="success"
              style={{ marginTop: 20 }}
              message="Upload complete"
              description={
                <Descriptions size="small" column={3} style={{ marginTop: 8 }}>
                  <Descriptions.Item label="Responses saved">{result.inserted}</Descriptions.Item>
                  <Descriptions.Item label="Dates">{result.date_from} to {result.date_to}</Descriptions.Item>
                  <Descriptions.Item label="Not mapped">
                    {result.unmatched > 0
                      ? <Tag color="orange">{result.unmatched} responses</Tag>
                      : <Tag color="green">0</Tag>}
                  </Descriptions.Item>
                  {result.skipped?.count > 0 && (
                    <Descriptions.Item label="Skipped (other call centers)" span={3}>
                      {result.skipped.locations.map(([name, n]) => `${name} (${n})`).join(', ')}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              }
            />
          )}
        </>
      )}

      {/* ── VIEW UPLOADED ─────────────────────────────────────────────────── */}
      {view === 'uploaded' && (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Text strong>Response date:</Text>
            <RangePicker value={range} onChange={setRange} allowClear={false} />
            {uploaded.length > 0 && <Text type="secondary">{uploaded.length} responses</Text>}
          </div>

          {uploadedLoading
            ? <Spin />
            : uploaded.length === 0
              ? <Text type="secondary">No responses stored in this date range.</Text>
              : (
                <Table
                  rowKey="ID"
                  dataSource={uploaded}
                  columns={uploadedCols}
                  size="small"
                  pagination={{ defaultPageSize: 50, showSizeChanger: true, showTotal: (t) => `${t} responses` }}
                  scroll={{ x: 'max-content' }}
                  sticky
                  bordered
                />
              )
          }
        </>
      )}

      {/* ── MEDALLIA ID MAPPING ───────────────────────────────────────────── */}
      {view === 'ids' && (
        <>
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            Set the <strong>Medallia ID</strong> for each CNE team member. This links uploaded survey responses to the
            right user; nothing needs re-uploading once an id is set. The Save button activates when a value changes.
          </Paragraph>

          {mapping.unmapped?.length > 0 && (
            <Alert
              type="warning"
              style={{ marginBottom: 16 }}
              message={`${mapping.unmapped.length} Medallia ID(s) in stored responses belong to nobody yet`}
              description={
                <Table
                  size="small"
                  rowKey="MedalliaID"
                  dataSource={mapping.unmapped}
                  pagination={false}
                  style={{ marginTop: 8, maxWidth: 520 }}
                  columns={[
                    { title: 'Medallia ID', dataIndex: 'MedalliaID', key: 'MedalliaID', width: 140,
                      render: (v) => <span style={{ fontFamily: 'monospace' }}>{v}</span> },
                    { title: 'Name in file', dataIndex: 'NameInFile', key: 'NameInFile' },
                    { title: 'Responses', dataIndex: 'Responses', key: 'Responses', width: 100, align: 'right' },
                  ]}
                />
              }
            />
          )}

          {mappingLoading
            ? <Spin />
            : (
              <Table
                rowKey="ID"
                dataSource={mapping.users}
                columns={userCols}
                size="small"
                pagination={false}
                bordered
                rowClassName={(r) => !r.IsActive ? 'ant-table-row-muted' : ''}
              />
            )
          }
        </>
      )}
    </>
  );
}
